import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/navigation';
import axios from 'axios';
import { logout } from '../../app/redux/slices/authSlice';

// Custom hook for API calls with Redux dispatch

const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const router = useRouter();
  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);

  // APIObject: { endpoint, method, successActionType, failureActionType }
  // params: request body or query params
  const makeApiCall = useCallback(
    async (apiObject, params = null) => {
      const {
        endpoint,
        params: configParams,
        successActionType,
        failureActionType,
        headers,
      } = apiObject;
      const method = endpoint.method || 'get';
      const url = endpoint.url;

      console.log('useApiCall: Making request', {
        method,
        url,
        data: method === 'get' ? undefined : params || configParams,
        params: method === 'get' ? params || configParams : undefined,
        headers,
      });

      setLoading(true);
      setError(null);
      try {
        const axiosConfig = {
          method,
          url,
          headers,
        };

        // For GET requests, use params (query string), for others use data (body)
        if (method === 'get') {
          // buildApiConfig already appends query string to URL for GET requests
          // So we don't need to pass params again
          // axiosConfig.params = params || configParams;
        } else {
          axiosConfig.data = params || configParams;
        }

        const response = await axios(axiosConfig);

        console.log('useApiCall: Response received', {
          url,
          status: response.status,
          data: response.data,
        });

        if (successActionType) {
          dispatch({ type: successActionType, payload: response.data });
        }
        return response.data;
      } catch (err) {
        console.error('useApiCall: Request failed', {
          url,
          error: err.response?.data || err.message,
          status: err.response?.status,
        });

        // Handle 401 Unauthorized — session expired / invalid token.
        // Only 401 triggers logout: 401 = not authenticated, force re-login.
        // 403 = authenticated but not authorized for this resource — should NOT
        // log the user out (e.g. a PB balance endpoint returning 403 must not
        // destroy the rider's session in projectL).
        // On 401: redirect to login WITHOUT destroying auth state (in case token is still valid)
        // User may have permission issues with specific endpoint, not necessarily session expired
        // On 401: redirect to login WITHOUT destroying auth state (in case token is still valid)
        // User may have permission issues with specific endpoint, not necessarily session expired
        if (err.response?.status === 401 && isLoggedIn) {
          console.log(`useApiCall: 401 detected on endpoint, checking current path`);

          const currentPath = window.location.pathname;
          if (currentPath === '/login') {
            console.log('useApiCall: Already on login page, ignoring 401');
            return;
          }

          // EXCEPTION: 401 on Wallet endpoints should NOT trigger a redirect.
          // The wallet service is often separate or handles auth differently.
          // Triggering a redirect on wallet failure kills the Payment page UX.
          const isWalletEndpoint = url.includes('/wallet/');
          if (isWalletEndpoint) {
            console.warn(`useApiCall: 401 on wallet endpoint [${url}]. Skipping redirect.`);
            setError(err);
            return;
          }

          // Show toast notification but don't logout
          try {
            const { emit } = require('@/utils/eventBus');
            emit('session-expired', {
              message: 'Session expired. Please login again.',
            });
          } catch (err) {
            console.error('[useApiCall] eventBus emit failed:', err);
          }

          // Redirect to login (user will re-authenticate)
          // Don't clear auth state in case the token is actually still valid
          setTimeout(() => {
            router.push('/login');
          }, 500);
        }

        setError(err);
        if (failureActionType) {
          dispatch({ type: failureActionType, payload: err });
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [dispatch, router, isLoggedIn]
  );

  return { loading, error, makeApiCall };
};

export default useApiCall;
